"use client";

import { Skeleton } from "@/components/ui/feedback/skeleton";
import { cn } from "@/lib/utils";

export type ProjectCardSkeletonProps = {
  className?: string;
};

/**
 * Loading placeholder matching ProjectCard layout (header, title, bottom stats).
 */
export function ProjectCardSkeleton({ className }: ProjectCardSkeletonProps) {
  return (
    <div
      className={cn(
        "flex flex-col justify-between rounded-3xl border border-transparent p-4",
        "bg-background/35 dark:bg-card/80 shadow-sm min-h-[200px] w-full",
        className,
      )}
      aria-hidden="true"
    >
      <div className="flex items-center justify-between gap-4">
        <Skeleton className="size-5 rounded-lg" />
        <Skeleton className="h-7 w-16 rounded-full" />
      </div>

      <div className="flex-1 mt-4">
        <Skeleton className="h-5 w-3/4 rounded-md" />
        <Skeleton className="mt-2 h-5 w-1/2 rounded-md" />
      </div>

      <div className="flex items-center gap-2 mt-0 pt-4">
        <Skeleton className="h-7 w-12 rounded-full" />
        <Skeleton className="h-7 w-12 rounded-full" />
        <Skeleton className="h-7 w-12 rounded-full" />
      </div>
    </div>
  );
}

export type ProjectCardsSkeletonGridProps = {
  count?: number;
  className?: string;
};

/** Grid of project card skeletons matching the projects page layout. */
export function ProjectCardsSkeletonGrid({
  count = 8,
  className,
}: ProjectCardsSkeletonGridProps) {
  return (
    <div
      className={cn("grid grid-cols-2 lg:grid-cols-4 gap-3", className)}
      role="status"
      aria-label="Loading projects"
    >
      {Array.from({ length: count }, (_, index) => (
        <ProjectCardSkeleton key={index} />
      ))}
    </div>
  );
}
